import { GridOptions } from 'ag-grid-community'
import _ from 'lodash'
import { MutableRefObject, useCallback, useEffect, useRef } from 'react'
import { WbsItemAdditionalPropertyLayoutEntity } from '../../../../domain/entity/WbsItemAdditionalPropertyLayoutEntity'
import { LEVEL_SEPARATOR } from '../../../containers/BulkSheet/const'
import { FlagxsColumnDef } from '../../../containers/BulkSheetView/gridOptions/extension'
import {
  ExcelImportColumnInfo,
  ExcelKeyMap,
  ExcelSource,
  useWbsItemExcelParser,
} from '../../../containers/BulkSheetView/hooks/wbsItemExcelParser'
import {
  EXTERNAL_ID_DISPLAY_NAME,
  EXTERNAL_ID_CODE,
  EXTERNAL_ID_TYPE,
  EXTERNAL_ID_TICKET_TYPE,
  EXTERNAL_ID_STATUS,
  EXTERNAL_ID_SUBSTATUS,
  EXTERNAL_ID_PRIORITY,
  EXTERNAL_ID_DIFFICULTY,
  EXTERNAL_ID_DESCRIPTION,
  EXTERNAL_ID_TEAM,
  EXTERNAL_ID_SPRINT,
  EXTERNAL_ID_ACCOUNTABLE,
  EXTERNAL_ID_RESPONSIBLE,
  EXTERNAL_ID_ASSIGNEE,
  EXTERNAL_ID_WATCHERS,
  EXTERNAL_ID_ESTIMATED_STORY_POINT,
  EXTERNAL_ID_DELIVERABLE_ESTIMATED_HOUR,
  EXTERNAL_ID_TASK_ESTIMATED_HOUR,
  EXTERNAL_ID_SCHEDULED_START_DATE,
  EXTERNAL_ID_SCHEDULED_END_DATE,
  EXTERNAL_ID_ACTUAL_START_DATE,
  EXTERNAL_ID_ACTUAL_END_DATE,
  EXTERNAL_ID_DELIVERABLE_ESTIMATED_AMOUNT,
  EXTERNAL_ID_TASK_ESTIMATED_AMOUNT,
  EXTERNAL_ID_ACTUAL_AMOUNT,
  EXTERNAL_ID_TAGS,
} from '../gridOptions/defaultColumns'
import {
  NewWbsItemRow,
  ProjectPlanNewRow,
  ProjectPlanRowBody,
} from '../projectPlanNew'
import store from '../../../../store'
import { WbsItemTypeVO } from '../../../../domain/value-object/WbsItemTypeVO'
import { WbsItemCodeGenerator } from '../../../../applications/ports/wbsItemCodeService'
import { generateUuid } from '../../../../utils/uuids'
import { WbsItemType } from '../../../../domain/entity/WbsItemEntity'
import { addGlobalMessage, MessageLevel } from '../../../../store/messages'
import { intl } from '../../../../i18n'

const DEFAULT_IMPORTABLE_COLUMN_EXTERNAL_IDS: string[] = [
  EXTERNAL_ID_DISPLAY_NAME,
  EXTERNAL_ID_CODE,
  EXTERNAL_ID_TYPE,
  EXTERNAL_ID_TICKET_TYPE,
  EXTERNAL_ID_STATUS,
  EXTERNAL_ID_SUBSTATUS,
  EXTERNAL_ID_PRIORITY,
  EXTERNAL_ID_DIFFICULTY,
  EXTERNAL_ID_DESCRIPTION,
  EXTERNAL_ID_TEAM,
  EXTERNAL_ID_SPRINT,
  EXTERNAL_ID_ACCOUNTABLE,
  EXTERNAL_ID_RESPONSIBLE,
  EXTERNAL_ID_ASSIGNEE,
  EXTERNAL_ID_WATCHERS,
  EXTERNAL_ID_ESTIMATED_STORY_POINT,
  EXTERNAL_ID_DELIVERABLE_ESTIMATED_HOUR,
  EXTERNAL_ID_TASK_ESTIMATED_HOUR,
  EXTERNAL_ID_SCHEDULED_START_DATE,
  EXTERNAL_ID_SCHEDULED_END_DATE,
  EXTERNAL_ID_ACTUAL_START_DATE,
  EXTERNAL_ID_ACTUAL_END_DATE,
  EXTERNAL_ID_DELIVERABLE_ESTIMATED_AMOUNT,
  EXTERNAL_ID_TASK_ESTIMATED_AMOUNT,
  EXTERNAL_ID_ACTUAL_AMOUNT,
  EXTERNAL_ID_TAGS,
]

type ParsedData = {
  messages: string[]
  updatedData: Map<string, ProjectPlanNewRow>
  addedChildMap: Map<string, ProjectPlanNewRow>
  addedNextSiblingMap: Map<string, ProjectPlanNewRow>
  addedRootList: ProjectPlanNewRow[]
}

export const useExcelImport = (
  projectUuid: string,
  gridOptions: GridOptions | undefined,
  contextRef: MutableRefObject<Record<string, any>>,
  data: ProjectPlanNewRow[],
  wbsItemAdditionalProperties:
    | WbsItemAdditionalPropertyLayoutEntity
    | undefined,
  setLoading: (val: boolean) => void,
  generateCode: WbsItemCodeGenerator,
  setData: (rows: ProjectPlanNewRow[]) => void,
  fetchBodyByCode: (codes: string[]) => Promise<void>,
  isFetchingBody: boolean,
  treeRootUuid?: string
) => {
  const {
    readExcelSource,
    getExcelImportColumnInfo,
    parseInteger,
    parseSprint,
    parseWorkLoadHour,
    findFromContext,
    mergeCommonWbsItemUpdatedRow,
    createErrorMessage,
    hasWbsItemRowDiff,
  } = useWbsItemExcelParser(projectUuid)

  const getLevel = useCallback(
    (source: ExcelSource, excelInfo: ExcelImportColumnInfo): number => {
      const treeStr =
        source[excelInfo.keyMap[EXTERNAL_ID_DISPLAY_NAME]]?.toString()
      return treeStr
        ? Array.from(treeStr.matchAll(new RegExp(LEVEL_SEPARATOR, 'g'))).length
        : 0
    },
    []
  )

  const getParentSource = useCallback(
    (
      allSource: ExcelSource[],
      currentSource: ExcelSource,
      excelInfo: ExcelImportColumnInfo,
      index: number
    ): ExcelSource | undefined => {
      const currentLevel = getLevel(currentSource, excelInfo)
      if (currentLevel === 0) return undefined
      for (let j = index - 1; 0 <= j; j--) {
        const prevLevel = getLevel(allSource[j], excelInfo)
        if (prevLevel === currentLevel - 1) {
          return allSource[j]
        }
      }
      return undefined
    },
    [getLevel]
  )

  const mergeUpdatedRow = useCallback(
    async (
      embodiedRow: Partial<NewWbsItemRow>,
      excelRow: ExcelSource,
      excelColInfo: ExcelImportColumnInfo,
      context: any
    ) => {
      const mergedRow = await mergeCommonWbsItemUpdatedRow(
        embodiedRow,
        excelRow,
        {
          code: EXTERNAL_ID_CODE,
          displayName: EXTERNAL_ID_DISPLAY_NAME,
          status: EXTERNAL_ID_STATUS,
          substatus: EXTERNAL_ID_SUBSTATUS,
          priority: EXTERNAL_ID_PRIORITY,
          difficulty: EXTERNAL_ID_DIFFICULTY,
          description: EXTERNAL_ID_DESCRIPTION,
          team: EXTERNAL_ID_TEAM,
          accountable: EXTERNAL_ID_ACCOUNTABLE,
          responsible: EXTERNAL_ID_RESPONSIBLE,
          assignee: EXTERNAL_ID_ASSIGNEE,
          watchers: EXTERNAL_ID_WATCHERS,
          estimatedStorypoint: EXTERNAL_ID_ESTIMATED_STORY_POINT,
          scheduledStartDate: EXTERNAL_ID_SCHEDULED_START_DATE,
          scheduledEndDate: EXTERNAL_ID_SCHEDULED_END_DATE,
          actualStartDate: EXTERNAL_ID_ACTUAL_START_DATE,
          actualEndDate: EXTERNAL_ID_ACTUAL_END_DATE,
          tag: EXTERNAL_ID_TAGS,
          sprint: EXTERNAL_ID_SPRINT,
        },
        excelColInfo,
        context
      )
      const keyDeliverableEstimatedHour =
        excelColInfo.keyMap[EXTERNAL_ID_DELIVERABLE_ESTIMATED_HOUR]
      const keyTaskEstimatedHour =
        excelColInfo.keyMap[EXTERNAL_ID_TASK_ESTIMATED_HOUR]
      const keyDeliverableEstimatedAmount =
        excelColInfo.keyMap[EXTERNAL_ID_DELIVERABLE_ESTIMATED_AMOUNT]
      const keyTaskEstimatedAmount =
        excelColInfo.keyMap[EXTERNAL_ID_TASK_ESTIMATED_AMOUNT]
      const keyActualAmount = excelColInfo.keyMap[EXTERNAL_ID_ACTUAL_AMOUNT]
      const hoursPerSelectedUnit =
        context['workloadUnitState']?.hoursPerSelectedUnit || 1

      return {
        ...embodiedRow,
        ...mergedRow,
        displayName: excelRow[excelColInfo.keyMap[EXTERNAL_ID_DISPLAY_NAME]]
          .toString()
          .replaceAll(LEVEL_SEPARATOR, ''),
        estimatedHour:
          embodiedRow.wbsItemType?.isDeliverable() &&
          keyDeliverableEstimatedHour
            ? parseWorkLoadHour(
                excelRow[keyDeliverableEstimatedHour],
                hoursPerSelectedUnit
              )
            : embodiedRow.wbsItemType?.isTask() && keyTaskEstimatedHour
              ? parseWorkLoadHour(
                  excelRow[keyTaskEstimatedHour],
                  hoursPerSelectedUnit
                )
              : embodiedRow.estimatedHour,
        estimatedAmount:
          embodiedRow.wbsItemType?.isDeliverable() &&
          keyDeliverableEstimatedAmount
            ? parseInteger(excelRow[keyDeliverableEstimatedAmount])
            : embodiedRow.wbsItemType?.isTask() && keyTaskEstimatedAmount
              ? parseInteger(excelRow[keyTaskEstimatedAmount])
              : embodiedRow.estimatedAmount,
        actualAmount: keyActualAmount
          ? parseInteger(excelRow[keyActualAmount])
          : embodiedRow.actualAmount,
      }
    },
    [mergeCommonWbsItemUpdatedRow, parseWorkLoadHour, parseInteger]
  )

  const hasDiff = useCallback(
    (bodyA: ProjectPlanRowBody, bodyB: ProjectPlanRowBody) => {
      const a = bodyA.wbsItem
      const b = bodyB.wbsItem
      const diff = hasWbsItemRowDiff(a, b)
      return diff || bodyA.sprint?.uuid !== bodyB.sprint?.uuid
    },
    [hasWbsItemRowDiff]
  )

  const validate = useCallback(
    (
      allExcelRow: ExcelSource[],
      currentExcelRow: ExcelSource,
      excelInfo: ExcelImportColumnInfo,
      rootData: ProjectPlanNewRow | undefined,
      typeMap: Map<string, WbsItemTypeVO>,
      currentLevel: number,
      prevLevel: number | undefined,
      index: number,
      isAdded: boolean,
      displayName: string,
      excelRowType: string,
      type: WbsItemTypeVO | undefined
    ) => {
      // Invalid tree
      if (isAdded && currentLevel - (prevLevel ?? currentLevel) > 1) {
        // 行追加の場合のみツリー構造（parent/prevSibling）の更新を行うので、行追加の場合のみチェック。
        // 行編集の場合はツリー構造（parent/prevSibling）の更新は行わず、行の中身（body）のみ更新している。
        return createErrorMessage(index + 1, 'projectPlan.import.invalid.tree')
      }
      // Name required
      if (!displayName) {
        return createErrorMessage(
          index + 1,
          'projectPlan.import.displayName.required'
        )
      }

      if (isAdded) {
        if (!excelRowType) {
          return createErrorMessage(
            index + 1,
            'projectPlan.import.type.required'
          )
        }

        // Invalid wbs type
        if (!type) {
          return createErrorMessage(
            index + 1,
            'projectPlan.import.invalid.ticketType'
          )
        }

        const parent = getParentSource(
          allExcelRow,
          currentExcelRow,
          excelInfo,
          index
        )
        if (!parent) {
          const rootType = rootData?.body?.wbsItem.wbsItemType

          // Invalid parent type
          const isInvalidParentType =
            rootType && !(rootType && type?.canBeChildOf(rootType))
          if (isInvalidParentType) {
            return createErrorMessage(
              index + 1,
              'projectPlan.import.invalid.parentType',
              {
                parentType: rootType.name,
                type: excelRowType,
              }
            )
          }

          // Invalid root type
          const isInvalidRootType =
            !rootType && !(type?.isProcess() || type?.isWorkgroup())
          if (isInvalidRootType) {
            return createErrorMessage(
              index + 1,
              'projectPlan.import.invalid.type',
              {
                type: type?.rootType,
              }
            )
          }
        }

        if (parent) {
          const parentType = typeMap.get(
            parent[excelInfo.keyMap[EXTERNAL_ID_TYPE]]
          )
          const isInvalidParentType =
            !parentType || !type?.canBeChildOf(parentType)
          if (isInvalidParentType) {
            return createErrorMessage(
              index + 1,
              'projectPlan.import.invalid.parentType',
              {
                parentType: parent[excelInfo.keyMap[EXTERNAL_ID_TYPE]],
                type: excelRowType,
              }
            )
          }
        }
      }
    },
    [createErrorMessage, getParentSource]
  )

  const fetchBodyByNotEmbodiedCodes = useFetchBodyByNotEmbodiedCodes(
    data,
    fetchBodyByCode,
    isFetchingBody
  )
  const dataRef = useRef(data)
  useEffect(() => {
    dataRef.current = data
  }, [data])
  const parse = useCallback(
    async (
      source: ExcelSource[],
      excelInfo: ExcelImportColumnInfo,
      treeRootUuid?: string,
      context?: any
    ): Promise<ParsedData> => {
      const result: ParsedData = {
        messages: [],
        updatedData: new Map<string, ProjectPlanNewRow>(),
        addedChildMap: new Map<string, ProjectPlanNewRow>(),
        addedNextSiblingMap: new Map<string, ProjectPlanNewRow>(),
        addedRootList: [],
      }

      let prevLevel: number | undefined
      let sourceTree: ProjectPlanNewRow[] = []
      const embodiedCodes: string[] = []
      dataRef.current.forEach((row: ProjectPlanNewRow) => {
        if (row.body && row.body.wbsItem.code) {
          embodiedCodes.push(row.body.wbsItem.code)
        }
      })
      const wbsItemTypes = store.getState().project.wbsItemTypes.getAll()
      const typeMap = new Map(wbsItemTypes.map(v => [v.name, v]))
      const excelKeyMap = excelInfo.keyMap

      await fetchBodyByNotEmbodiedCodes(source, excelKeyMap, embodiedCodes)

      const embodiedRows: ProjectPlanNewRow[] = []
      let rootData: ProjectPlanNewRow | undefined = undefined
      dataRef.current.forEach((row: ProjectPlanNewRow) => {
        if (!row.body?.wbsItem) return
        embodiedRows.push(row)
        if (treeRootUuid && row.uuid === treeRootUuid) {
          rootData = row
        }
      })

      for (let index = 0; index < source.length; index++) {
        const excelRow = source[index]
        const currentLevel = getLevel(excelRow, excelInfo)
        const treeStr =
          excelRow[excelKeyMap[EXTERNAL_ID_DISPLAY_NAME]]?.toString()
        const displayName = treeStr?.replaceAll(LEVEL_SEPARATOR, '')
        const excelRowType = excelRow[excelKeyMap[EXTERNAL_ID_TYPE]]
        const type: WbsItemTypeVO | undefined = typeMap.get(excelRowType)
        try {
          const isAdded = !excelRow[excelKeyMap[EXTERNAL_ID_CODE]]
          // Validation
          const message = validate(
            source,
            excelRow,
            excelInfo,
            rootData,
            typeMap,
            currentLevel,
            prevLevel,
            index,
            isAdded,
            displayName,
            excelRowType,
            type
          )
          if (message) result.messages.push(message)
          if (isAdded) {
            excelRow[excelInfo.keyMap[EXTERNAL_ID_CODE]] = generateCode()
          }
          if (result.messages.length) continue

          // Prepare import
          if (!isAdded) {
            // Edited data
            const embodiedRow: ProjectPlanNewRow | undefined =
              embodiedRows.find(
                v =>
                  v.body!.wbsItem.code ===
                  excelRow[excelInfo.keyMap[EXTERNAL_ID_CODE]]?.toString()
              )
            if (!embodiedRow) {
              continue
            }
            const mergedWbsItem = await mergeUpdatedRow(
              embodiedRow.body!.wbsItem,
              excelRow,
              excelInfo,
              context
            )
            const newRow = {
              ...embodiedRow,
              edited: true,
              body: {
                ...embodiedRow.body,
                wbsItem: mergedWbsItem,
                sprint: parseSprint(
                  excelRow[excelInfo.keyMap[EXTERNAL_ID_SPRINT]],
                  mergedWbsItem.team,
                  context
                ),
              },
            } as ProjectPlanNewRow
            if (hasDiff(embodiedRow.body!, newRow.body!)) {
              result.updatedData.set(embodiedRow.uuid, newRow)
            }
            sourceTree.splice(currentLevel, sourceTree.length, newRow)
          } else {
            // Added data
            const uuid = generateUuid()
            const wbsItemUuid = generateUuid()
            const rootType = type!.isWorkgroup()
              ? WbsItemType.PROCESS
              : type!.rootType

            const newWbsItem = await mergeUpdatedRow(
              {
                uuid: wbsItemUuid,
                wbsItemType: type,
                baseWbsItemType: type,
                type: rootType,
              },
              excelRow,
              excelInfo,
              context
            )
            const newRow = {
              uuid,
              wbsItemUuid,
              type: rootType,
              added: true,
              body: {
                wbsItem: newWbsItem,
                sprint: parseSprint(
                  excelRow[excelInfo.keyMap[EXTERNAL_ID_SPRINT]],
                  findFromContext(
                    context,
                    'team',
                    excelRow[excelInfo.keyMap[EXTERNAL_ID_TEAM]]
                  ),
                  context
                ),
              } as ProjectPlanRowBody,
            } as ProjectPlanNewRow

            if (prevLevel === currentLevel - 1) {
              const parentRow = sourceTree[sourceTree.length - 1]
              result.addedChildMap.set(parentRow.uuid, newRow)
            } else if (prevLevel === currentLevel) {
              const prevSiblingRow = sourceTree[sourceTree.length - 1]
              result.addedNextSiblingMap.set(prevSiblingRow.uuid, newRow)
            } else {
              const prevSiblingRow = sourceTree[currentLevel]
              if (prevSiblingRow) {
                result.addedNextSiblingMap.set(prevSiblingRow.uuid, newRow)
              } else {
                result.addedRootList.push(newRow)
              }
            }
            sourceTree.splice(currentLevel, sourceTree.length, newRow)
          }
        } finally {
          prevLevel = currentLevel
        }
      }
      return result
    },
    [
      validate,
      getLevel,
      generateCode,
      mergeUpdatedRow,
      parseSprint,
      hasDiff,
      findFromContext,
      fetchBodyByNotEmbodiedCodes,
    ]
  )

  const createRowByParsedData = useCallback(
    (
      rows: ProjectPlanNewRow[],
      parsedData: ParsedData,
      treeRootUuid?: string
    ): ProjectPlanNewRow[] => {
      const addNext = (row: ProjectPlanNewRow) => {
        const childRow = parsedData.addedChildMap.get(row.uuid)
        if (childRow) {
          childRow.treeValue = [...row.treeValue, childRow.uuid]
          newData.push(childRow)
          addNext(childRow)
          parsedData.addedChildMap.delete(row.uuid)
        }
        const nextRow = parsedData.addedNextSiblingMap.get(row.uuid)
        if (nextRow) {
          nextRow.treeValue = [
            ...row.treeValue.slice(0, row.treeValue.length - 1),
            nextRow.uuid,
          ]
          newData.push(nextRow)
          addNext(nextRow)
          parsedData.addedNextSiblingMap.delete(row.uuid)
        }
      }
      const newData: ProjectPlanNewRow[] = []
      rows.forEach(row => {
        const updated = parsedData.updatedData.get(row.uuid)
        if (updated) {
          newData.push(updated)
        } else {
          newData.push(row)
        }
        addNext(row)
      })
      parsedData.addedRootList.forEach(row => {
        row.treeValue = [...(treeRootUuid ? [treeRootUuid] : []), row.uuid]
        newData.push(row)
        addNext(row)
      })
      return newData
    },
    []
  )

  const onExcelImport = useCallback(
    async (raw: Uint8Array) => {
      // if (!gridOptions?.context || !gridOptions?.columnDefs) return
      setLoading(true)
      setTimeout(async () => {
        try {
          // Convert excel file
          const source: ExcelSource[] = readExcelSource(raw)
          // Parse
          const excelInfo: ExcelImportColumnInfo =
            await getExcelImportColumnInfo(
              DEFAULT_IMPORTABLE_COLUMN_EXTERNAL_IDS,
              gridOptions?.columnDefs as FlagxsColumnDef[],
              source,
              wbsItemAdditionalProperties
            )

          const parsedData = await parse(
            source.slice(1),
            excelInfo,
            treeRootUuid,
            contextRef.current
          )
          if (parsedData.messages.length > 0) {
            store.dispatch(
              addGlobalMessage({
                type: MessageLevel.WARN,
                title: intl.formatMessage({ id: 'projectPlan.import.warning' }),
                text: parsedData.messages.join('\n'),
              })
            )
            return
          }
          // Convert
          const newData = createRowByParsedData(data, parsedData)
          newData.length > 1500 && setData([]) // Prevent slow processing
          setData(newData)
        } finally {
          setLoading(false)
        }
      }, 300)
    },
    [
      data,
      gridOptions,
      contextRef,
      treeRootUuid,
      wbsItemAdditionalProperties,
      setLoading,
      readExcelSource,
      getExcelImportColumnInfo,
      parse,
      createRowByParsedData,
      setData,
    ]
  )

  return onExcelImport
}

const useFetchBodyByNotEmbodiedCodes = (
  data: ProjectPlanNewRow[],
  fetchBodyByCode: (codes: string[]) => Promise<void>,
  isFetchingBody: boolean
) => {
  const isFetchingBodyRef = useRef(isFetchingBody)
  useEffect(() => {
    isFetchingBodyRef.current = isFetchingBody
  }, [isFetchingBody])
  const dataRef = useRef(data)
  useEffect(() => {
    dataRef.current = data
  }, [data])

  const fetchBodyByNotEmbodiedCodes = useCallback(
    async (
      source: ExcelSource[],
      excelKeyMap: ExcelKeyMap,
      embodiedCodes: string[]
    ) => {
      const notEmbodiedCodes: string[] = []
      for (let index = 0; index < source.length; index++) {
        const excelRow = source[index]
        const wbsItemCode = excelRow[excelKeyMap[EXTERNAL_ID_CODE]]
        if (wbsItemCode && !embodiedCodes.includes(wbsItemCode)) {
          notEmbodiedCodes.push(wbsItemCode)
        }
      }
      if (notEmbodiedCodes.length > 0) {
        const chunks = _.chunk(notEmbodiedCodes, 500)
        for (let i = 0; i < chunks.length; i++) {
          await fetchBodyByCode(chunks[i])
        }
      }

      while (isFetchingBodyRef.current) {
        await new Promise(resolve => setTimeout(resolve, 100))
      }
    },
    [fetchBodyByCode]
  )

  return fetchBodyByNotEmbodiedCodes
}
